import { useState } from 'react';
import { Icon } from '@iconify/react';
import axiosInstance from '../../services/axios.config';
import { publishMissionPreview, CompiledMission } from '../../services/mission-preview';

interface SecurityProps {
  onClose?: () => void;
}

type SecurityType =
  | 'patrol'
  | 'perimeter'
  | 'alert'
  | 'surveillance'
  | 'access';

type Priority =
  | 'low'
  | 'normal'
  | 'high'
  | 'critical';

export default function Security({ onClose }: SecurityProps) {
  const [securityType, setSecurityType] =
    useState<SecurityType>('patrol');

  const [priority, setPriority] =
    useState<Priority>('normal');

  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLaunch = async () => {
    if (loading) return;

    setLoading(true);
    setError('');

    try {
      const typeMap: Record<SecurityType, string> = {
        patrol: 'Patrol',
        perimeter: 'Perimeter Check',
        alert: 'Alert Response',
        surveillance: 'Surveillance',
        access: 'Access Control'
      };

      const response = await axiosInstance.post('/yards/security/generate', {
        security_type: typeMap[securityType],
        priority: priority.charAt(0).toUpperCase() + priority.slice(1),
        notes
      });

      if (response.data?.success !== true || !response.data?.mission) {
        throw new Error(response.data?.error || 'Security route generation failed');
      }

      const compiled = await axiosInstance.post<CompiledMission>(
        '/yards/mission/compile',
        { mission: response.data.mission }
      );

      if (compiled.data?.success !== true) {
        throw new Error('Security mission validation failed');
      }

      publishMissionPreview(compiled.data);
      onClose?.();
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
        err.message ||
        'Failed to generate Security flight preview'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50 p-4">

      <div className="w-full max-w-[720px] rounded-lg bg-white shadow-xl">

        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">

          <div>
            <h2 className="text-2xl font-bold text-black-1 font-[Geist-Bold]">
              Security Mission
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Configure a Willow Yard security drone mission
            </p>
          </div>

          <button
            onClick={onClose}
            className="text-gray-500 hover:text-black"
          >
            <Icon
              icon="gridicons:cross"
              width={28}
              height={28}
            />
          </button>

        </div>


        {/* Main Content */}
        <div className="p-6">

          {/* Security Type */}
          <div>
            <h3 className="mb-3 text-base font-semibold text-black-1">
              Security Type
            </h3>

            <div className="space-y-3">

              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="radio"
                  name="securityType"
                  checked={securityType === 'patrol'}
                  onChange={() => setSecurityType('patrol')}
                />
                <span>Patrol</span>
              </label>

              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="radio"
                  name="securityType"
                  checked={securityType === 'perimeter'}
                  onChange={() => setSecurityType('perimeter')}
                />
                <span>Perimeter Check</span>
              </label>

              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="radio"
                  name="securityType"
                  checked={securityType === 'alert'}
                  onChange={() => setSecurityType('alert')}
                />
                <span>Alert Response</span>
              </label>

              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="radio"
                  name="securityType"
                  checked={securityType === 'surveillance'}
                  onChange={() => setSecurityType('surveillance')}
                />
                <span>Surveillance</span>
              </label>

              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="radio"
                  name="securityType"
                  checked={securityType === 'access'}
                  onChange={() => setSecurityType('access')}
                />
                <span>Access Control</span>
              </label>

            </div>
          </div>


          {/* Priority */}
          <div className="mt-7">

            <h3 className="mb-3 text-base font-semibold text-black-1">
              Priority Level
            </h3>

            <div className="grid grid-cols-2 gap-3 rounded border border-gray-200 p-4 sm:grid-cols-4">

              {(['low', 'normal', 'high', 'critical'] as Priority[]).map(
                (level) => (
                  <label
                    key={level}
                    className="flex cursor-pointer items-center gap-2"
                  >
                    <input
                      type="radio"
                      name="priority"
                      checked={priority === level}
                      onChange={() => setPriority(level)}
                    />

                    <span className="capitalize">
                      {level}
                    </span>
                  </label>
                )
              )}

            </div>
          </div>


          {/* Notes */}
          <div className="mt-7">

            <label
              htmlFor="securityNotes"
              className="mb-2 block text-base font-semibold text-black-1"
            >
              Notes
            </label>

            <textarea
              id="securityNotes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={5}
              placeholder="Enter mission instructions, location details, or security concerns..."
              className="w-full resize-none rounded border border-gray-300 p-3 outline-none focus:border-blue"
            />

          </div>


          {/* Drone Status */}
          <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4">

            <div className="flex items-center justify-between gap-4">

              <div>
                <p className="font-semibold text-black-1">
                  Security Drone
                </p>

                <p className="mt-1 text-sm text-gray-500">
                  Drone mission integration is not connected yet.
                </p>
              </div>

              <span className="rounded bg-gray-200 px-3 py-1 text-sm text-gray-600">
                Not Connected
              </span>

            </div>

          </div>

        </div>


        {error && (
          <div role="alert" className="mx-6 mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t px-6 py-4">

          <button
            onClick={onClose}
            className="rounded px-5 py-2 text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>

          <button
            onClick={handleLaunch}
            disabled={loading}
            className="rounded bg-blue px-6 py-2 font-semibold text-white hover:opacity-90"
          >
            {loading ? "Generating Preview..." : "Generate Flight Preview"}
          </button>

        </div>

      </div>

    </div>
  );
}